/* eslint-disable react-refresh/only-export-components -- co-locacion intencional de componente + helpers/hooks muy usados; la regla es solo de Fast Refresh (dev), sin impacto en produccion */
/**
 * `ConfirmDialog` y `PromptDialog` — reemplazo de `window.confirm` y
 * `window.prompt` con diálogos Fluent UI accesibles.
 *
 * Patrón provider + hook con API basada en promesas:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title, message, intent: "warning" })) { ... }
 *
 *   const prompt = usePrompt();
 *   const motivo = await prompt({ title, placeholder, minLength: 5 });
 *   if (motivo) { ... }   // null si el usuario canceló
 *
 * Un único `<ConfirmDialogProvider>` montado al pie del árbol React (en
 * `App.tsx`) mantiene la cola de solicitudes y resuelve cada promise cuando
 * el usuario confirma o cancela.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Textarea,
  Text,
} from "@fluentui/react-components";

// ── Tipos públicos ──────────────────────────────────────────────────────────

export interface ConfirmOptions {
  /** Título del diálogo (línea superior, en negrita). */
  title: string;
  /** Texto explicativo. Acepta múltiples párrafos con `\n\n`. */
  message?: string;
  /** Etiqueta del botón de confirmar. Default: "Confirmar". */
  confirmLabel?: string;
  /** Etiqueta del botón de cancelar. Default: "Cancelar". */
  cancelLabel?: string;
  /** Intención visual del botón confirmar. */
  intent?: "info" | "warning" | "danger";
}

export interface PromptOptions {
  title: string;
  message?: string;
  /** Texto guía dentro del campo. */
  placeholder?: string;
  /** Valor inicial del campo. */
  defaultValue?: string;
  /** Longitud mínima requerida para habilitar el botón confirmar. */
  minLength?: number;
  /** Longitud máxima del texto (default 5000). */
  maxLength?: number;
  /** Si `true` renderiza un `<Textarea>` en vez de un `<Input>`. */
  multiline?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}

// ── Contexto ────────────────────────────────────────────────────────────────

interface ConfirmDialogContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

/** Devuelve la función `confirm({ ... })`. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error("useConfirm() debe usarse dentro de <ConfirmDialogProvider>.");
  }
  return ctx.confirm;
}

/** Devuelve la función `prompt({ ... })`. Resuelve `null` al cancelar. */
export function usePrompt(): (opts: PromptOptions) => Promise<string | null> {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error("usePrompt() debe usarse dentro de <ConfirmDialogProvider>.");
  }
  return ctx.prompt;
}

// ── Provider ────────────────────────────────────────────────────────────────

type Pendiente =
  | {
      kind: "confirm";
      opts: ConfirmOptions;
      resolve: (v: boolean) => void;
    }
  | {
      kind: "prompt";
      opts: PromptOptions;
      resolve: (v: string | null) => void;
    };

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const [valor, setValor] = useState<string>("");
  // Cola simple: si llega una segunda solicitud mientras hay una activa, se
  // encola y se procesa cuando la actual cierre. `useRef` evita re-renders.
  const cola = useRef<Pendiente[]>([]);

  const arrancarSiguiente = useCallback(() => {
    const siguiente = cola.current.shift();
    if (!siguiente) {
      setPendiente(null);
      setValor("");
      return;
    }
    if (siguiente.kind === "prompt") {
      setValor(siguiente.opts.defaultValue ?? "");
    } else {
      setValor("");
    }
    setPendiente(siguiente);
  }, []);

  const encolar = useCallback(
    (req: Pendiente) => {
      if (pendiente) {
        cola.current.push(req);
      } else {
        if (req.kind === "prompt") setValor(req.opts.defaultValue ?? "");
        setPendiente(req);
      }
    },
    [pendiente],
  );

  const confirm = useCallback(
    (opts: ConfirmOptions): Promise<boolean> =>
      new Promise((resolve) => encolar({ kind: "confirm", opts, resolve })),
    [encolar],
  );

  const prompt = useCallback(
    (opts: PromptOptions): Promise<string | null> =>
      new Promise((resolve) => encolar({ kind: "prompt", opts, resolve })),
    [encolar],
  );

  const ctx = useMemo<ConfirmDialogContextValue>(() => ({ confirm, prompt }), [confirm, prompt]);

  // ── Render del diálogo activo ────────────────────────────────────────────
  const cerrarConfirmacion = (resultado: boolean) => {
    if (pendiente?.kind === "confirm") pendiente.resolve(resultado);
    arrancarSiguiente();
  };
  const cerrarPrompt = (resultado: string | null) => {
    if (pendiente?.kind === "prompt") pendiente.resolve(resultado);
    arrancarSiguiente();
  };

  return (
    <ConfirmDialogContext.Provider value={ctx}>
      {children}
      {pendiente?.kind === "confirm" && (
        <ConfirmDialogUI
          opts={pendiente.opts}
          onAccept={() => cerrarConfirmacion(true)}
          onCancel={() => cerrarConfirmacion(false)}
        />
      )}
      {pendiente?.kind === "prompt" && (
        <PromptDialogUI
          opts={pendiente.opts}
          valor={valor}
          onChange={setValor}
          onAccept={() => cerrarPrompt(valor)}
          onCancel={() => cerrarPrompt(null)}
        />
      )}
    </ConfirmDialogContext.Provider>
  );
}

// ── Componentes internos ────────────────────────────────────────────────────

function ConfirmDialogUI({
  opts,
  onAccept,
  onCancel,
}: {
  opts: ConfirmOptions;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const intent = opts.intent ?? "info";
  const colorBoton =
    intent === "danger"
      ? "secondary"
      : intent === "warning"
        ? "primary"
        : "primary";
  return (
    <Dialog
      open
      onOpenChange={(_, d) => {
        if (!d.open) onCancel();
      }}
      modalType="modal"
    >
      <DialogSurface style={{ maxWidth: 460 }}>
        <DialogBody>
          <DialogTitle>{opts.title}</DialogTitle>
          {opts.message && (
            <DialogContent>
              {opts.message.split("\n\n").map((parrafo, i) => (
                <Text
                  key={i}
                  as="p"
                  style={{
                    margin: i === 0 ? 0 : "0.75rem 0 0 0",
                    whiteSpace: "pre-wrap",
                    color: intent === "danger" ? "#7B1A12" : "#1a1a1a",
                  }}
                >
                  {parrafo}
                </Text>
              ))}
            </DialogContent>
          )}
          <DialogActions>
            <Button appearance="subtle" onClick={onCancel}>
              {opts.cancelLabel ?? "Cancelar"}
            </Button>
            <Button
              appearance={colorBoton}
              onClick={onAccept}
              style={
                intent === "danger"
                  ? { background: "#C0392B", color: "white" }
                  : undefined
              }
            >
              {opts.confirmLabel ?? "Confirmar"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function PromptDialogUI({
  opts,
  valor,
  onChange,
  onAccept,
  onCancel,
}: {
  opts: PromptOptions;
  valor: string;
  onChange: (v: string) => void;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const minLen = opts.minLength ?? 0;
  const maxLen = opts.maxLength ?? 5000;
  const cumpleLongitud = valor.trim().length >= minLen;
  return (
    <Dialog
      open
      onOpenChange={(_, d) => {
        if (!d.open) onCancel();
      }}
      modalType="modal"
    >
      <DialogSurface style={{ maxWidth: 520 }}>
        <DialogBody>
          <DialogTitle>{opts.title}</DialogTitle>
          <DialogContent>
            {opts.message && (
              <Text as="p" style={{ margin: "0 0 0.75rem 0", whiteSpace: "pre-wrap" }}>
                {opts.message}
              </Text>
            )}
            {opts.multiline ? (
              <Textarea
                value={valor}
                onChange={(_, d) => onChange(d.value.slice(0, maxLen))}
                placeholder={opts.placeholder ?? ""}
                rows={6}
                resize="vertical"
                style={{ width: "100%" }}
                autoFocus
              />
            ) : (
              <Input
                value={valor}
                onChange={(_, d) => onChange(d.value.slice(0, maxLen))}
                placeholder={opts.placeholder ?? ""}
                style={{ width: "100%" }}
                autoFocus
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {minLen > 0 && !cumpleLongitud ? (
                <Text size={100} style={{ color: "#C0392B" }}>
                  Mínimo {minLen} caracteres ({valor.trim().length} actual).
                </Text>
              ) : (
                <span />
              )}
              <Text size={100} style={{ color: valor.length >= maxLen ? "#C0392B" : "#888" }}>
                {valor.length} / {maxLen}
              </Text>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" onClick={onCancel}>
              {opts.cancelLabel ?? "Cancelar"}
            </Button>
            <Button
              appearance="primary"
              onClick={onAccept}
              disabled={!cumpleLongitud}
            >
              {opts.confirmLabel ?? "Aceptar"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
